// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";

import type { AuthChangeEvent, Session } from "@supabase/supabase-js";
import { supabase } from "../supabaseClient";

import type { PlanKey } from "../config/plans";

/* =======================
   TIPOS
======================= */

/**
 * ✅ OJO:
 * - El plan "real" debe venir de /api/me (backend) para evitar desalineaciones.
 * - Aquí dejamos plan como "hint" opcional (por si lo tienes en metadata),
 *   pero NO debe usarse como fuente única de verdad.
 */
type User = {
  id: string;
  email: string | null;
  plan?: PlanKey; // ✅ hint/legacy opcional
};

export type LoginResult =
  | { ok: true }
  | {
      ok: false;
      reason?: "invalid_credentials" | "email_not_confirmed" | "unknown";
      message?: string;
    };

export type RegisterResult = {
  ok: boolean;
  needsEmailConfirm: boolean;
};

type AuthContextType = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  register: (email: string, password: string) => Promise<RegisterResult>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthContextType | null>(null);

function normalizePlan(v: unknown): PlanKey | undefined {
  return v === "free" || v === "basic" || v === "pro" || v === "premium" ? v : undefined;
}

/**
 * ✅ Convierte Session -> User
 * - Extrae plan SOLO si existe en metadata.
 * - Si no existe, NO inventa "free".
 */
function toUser(s: Session | null): User | null {
  const u = s?.user;
  if (!u) return null;

  const metaPlan =
    normalizePlan((u.user_metadata as any)?.plan) ??
    normalizePlan((u.app_metadata as any)?.plan);

  return {
    id: u.id,
    email: u.email ?? null,
    plan: metaPlan,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((s: Session | null) => {
    setSession(s);
    setUser(toUser(s));
  }, []);

  useEffect(() => {
    let mounted = true;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(
      (_event: AuthChangeEvent, newSession: Session | null) => {
        if (!mounted) return;
        applySession(newSession ?? null);
        setLoading(false);
      }
    );

    (async () => {
      const { data, error } = await supabase.auth.getSession();
      if (!mounted) return;

      if (error) {
        console.error("Error obteniendo sesión:", error);
        applySession(null);
        setLoading(false);
        return;
      }

      applySession(data.session ?? null);
      setLoading(false);
    })();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [applySession]);

  /* =======================
     LOGIN
  ======================= */
  const login = async (email: string, password: string): Promise<LoginResult> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error || !data.session || !data.user) {
      const msg = error?.message?.toLowerCase() || "";
      if (msg.includes("confirm") && msg.includes("email")) {
        return { ok: false, reason: "email_not_confirmed" };
      }
      if (msg.includes("invalid") || msg.includes("credentials")) {
        return { ok: false, reason: "invalid_credentials" };
      }

      return { ok: false, reason: "unknown", message: error?.message };
    }

    applySession(data.session);
    return { ok: true };
  };

  /* =======================
     REGISTER
  ======================= */
  const register = async (email: string, password: string): Promise<RegisterResult> => {
    // ✅ IMPORTANTÍSIMO:
    // Para confirmación email / magic links / OAuth, Supabase debe volver a una ruta callback.
    // Así distinguimos "recovery" vs "signup" y evitamos mandar a UpdatePassword por error.
    const emailRedirectTo = new URL("/auth/callback", window.location.origin).toString();

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo },
    });

    if (error || !data.user) {
      console.error("Register error:", error);
      return { ok: false, needsEmailConfirm: false };
    }

    const needsEmailConfirm = !data.session;

    applySession(data.session ?? null);
    return { ok: true, needsEmailConfirm };
  };

  /* =======================
     LOGOUT
  ======================= */
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error("Logout error:", error);
    } finally {
      // ✅ Siempre limpia estado local
      applySession(null);
    }
  };

  /* =======================
     ACCESS TOKEN
  ======================= */
  const getAccessToken = async (): Promise<string | null> => {
    // ✅ Siempre consulta el session actual (más robusto ante refresh interno)
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error("Error obteniendo access_token:", error);
      return null;
    }
    return data.session?.access_token ?? null;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        login,
        register,
        logout,
        getAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth debe estar dentro de AuthProvider");
  }
  return ctx;
}





